import { useEffect, useState } from "react";
import { api } from "../api.js";

/**
 * Chronological audit trail for a single submission.
 *
 * Fetches /api/submissions/:id/timeline and renders a vertical list of
 * events (submit → return → resubmit → approve). Used on both Submit.jsx
 * (so a worker sees why their report was returned) and the supervisor
 * review detail (so a supervisor sees the full counter-signing history).
 *
 * Server-side access control already scopes the endpoint: owner + their
 * supervisor + admin can read it; anyone else gets 403. We still handle
 * the error defensively so a 403 doesn't blank out the whole screen.
 */
const ACTION_META = {
  submit: {
    label: "Submitted",
    verb: "submitted the report",
    ring: "bg-sky-100 text-sky-700 ring-sky-200",
  },
  return: {
    label: "Returned",
    verb: "returned the report for revisions",
    ring: "bg-amber-100 text-amber-800 ring-amber-200",
  },
  approve: {
    label: "Approved",
    verb: "approved and counter-signed",
    ring: "bg-emerald-100 text-emerald-700 ring-emerald-200",
  },
};

function formatInstant(iso) {
  if (!iso) return "";
  // Shorthand: "Apr 17, 2:03 PM" — fine for a workers-and-supervisors UI,
  // no need for full ISO. Falls back gracefully if the browser lacks
  // Intl.DateTimeFormat support (ancient mobile webviews).
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(d);
  } catch {
    return iso.slice(0, 16).replace("T", " ");
  }
}

function actorLabel(actor, fallback) {
  if (!actor) return fallback || "System";
  const name = actor.name || actor.email || "Someone";
  if (actor.role === "supervisor") return `${name} (supervisor)`;
  if (actor.role === "admin") return `${name} (admin)`;
  return name;
}

export default function SubmissionTimeline({ submissionId }) {
  const [events, setEvents] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await api.getSubmissionTimeline(submissionId);
        if (!cancelled) setEvents(d.events);
      } catch (e) {
        // Non-fatal: the rest of the page still works without the timeline.
        // Surface a small inline notice so a real bug doesn't hide.
        if (!cancelled) setErr(e.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [submissionId]);

  if (err) {
    return (
      <div className="card">
        <div className="text-sm font-semibold text-slate-700">History</div>
        <div className="mt-2 text-xs text-slate-500">
          Couldn't load history: {err}
        </div>
      </div>
    );
  }

  if (events === null) {
    return (
      <div className="card">
        <div className="text-sm font-semibold text-slate-700">History</div>
        <div className="mt-2 text-xs text-slate-500">Loading history…</div>
      </div>
    );
  }

  if (events.length === 0) {
    // Shouldn't happen in practice — a submission always logs a `submit`.
    // Shown defensively so a missing audit row doesn't render as an empty
    // card with no label.
    return (
      <div className="card">
        <div className="text-sm font-semibold text-slate-700">History</div>
        <div className="mt-2 text-xs text-slate-500">
          No audit events recorded yet.
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="text-sm font-semibold text-slate-700">History</div>
      <ol className="mt-3 space-y-3">
        {events.map((ev) => {
          const meta = ACTION_META[ev.action] || {
            label: ev.action,
            verb: ev.action,
            ring: "bg-slate-100 text-slate-700 ring-slate-200",
          };
          // Pull the return reason out of the after_json payload if the
          // server recorded one. Shown under the event so a worker doesn't
          // have to hunt through the page for feedback.
          const returnedReason =
            ev.action === "return"
              ? ev.after?.returned_reason || ev.after?.returnedReason || null
              : null;
          return (
            <li key={ev.id} className="flex gap-3">
              <span
                className={`mt-0.5 inline-flex h-6 shrink-0 items-center rounded-full px-2 text-[10px] font-semibold uppercase tracking-wide ring-1 ring-inset ${meta.ring}`}
              >
                {meta.label}
              </span>
              <div className="min-w-0 flex-1 text-sm">
                <div className="text-slate-800">
                  <span className="font-medium">
                    {actorLabel(ev.actor, "Worker")}
                  </span>{" "}
                  <span className="text-slate-500">{meta.verb}</span>
                </div>
                <div className="text-xs text-slate-500">
                  {formatInstant(ev.at)}
                </div>
                {returnedReason && (
                  <div className="mt-1 whitespace-pre-wrap rounded bg-amber-50 px-2 py-1 text-xs text-amber-900">
                    {returnedReason}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
